import { useNavigate } from 'react-router-dom';
import { MapPin, Smartphone, HelpCircle, Package, MessageCircle, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

export default function SupportPage() {
  const navigate = useNavigate();

  const faqs = [
    {
      q: 'إزاي أقدر أتابع شحنتي؟',
      a: 'يمكنك تتبع شحنتك في أي وقت من صفحة "تتبع شحنتك" باستخدام رقم التتبع الخاص بالشحنة.',
    },
    {
      q: 'كام مدة توصيل الشحنة؟',
      a: 'مدة التوصيل المتوقعة تتراوح بين يوم إلى 3 أيام عمل حسب المحافظة الوجهة.',
    },
    {
      q: 'هل يوجد خدمة تحصيل عند الاستلام (COD)؟',
      a: 'نعم، نوفر نظام تحصيل آمن عند الاستلام مع تحويل المبالغ لحسابك بشكل دوري ومنتظم.',
    },
    {
      q: 'إزاي أقدر أطلب إرجاع شحنة؟',
      a: 'يمكنك التواصل مع فريق الدعم مع تزويدهم برقم تتبع الشحنة وسبب الإرجاع، وسنقوم بمتابعة طلبك فورًا.',
    },
  ];

  return (
    <div className="min-h-screen bg-white" dir="rtl">
      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <img src="/icon-512-from-server.png" alt="Target Express Logo" className="h-12 w-12 hover:rotate-12 transition-transform duration-500" />
            <div className="hidden sm:block">
              <span className="text-xl font-bold text-gray-900 block leading-tight">Target Express</span>
              <span className="text-[10px] text-gray-500 block leading-tight">Shipping, Logistics &amp; Fulfillment Solutions</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/login')}>تسجيل الدخول</Button>
            <Button className="bg-orange-600 hover:bg-orange-700" onClick={() => navigate('/register')}>
              ابدأ الشحن الآن
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 text-white py-20">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-2xl mb-6">
            <HelpCircle className="w-8 h-8" />
          </div>
          <h1 className="text-4xl font-bold mb-4">مركز المساعدة</h1>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            إجابات على أكثر الأسئلة شيوعًا، ولو احتجت مساعدة إضافية فريقنا جاهز لخدمتك.
          </p>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="border-0 shadow-md hover:shadow-lg transition cursor-pointer" onClick={() => navigate('/track')}>
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Package className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">تتبع شحنتك</h3>
                <p className="text-sm text-gray-600">تابع حالة شحنتك لحظة بلحظة</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-md hover:shadow-lg transition cursor-pointer" onClick={() => navigate('/contact')}>
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <MessageCircle className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">تواصل معنا</h3>
                <p className="text-sm text-gray-600">فريقنا جاهز للرد على استفساراتك</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">الأسئلة الشائعة</h2>
          <div className="space-y-4">
            {faqs.map((item, idx) => (
              <div key={idx} className="border border-gray-200 rounded-xl p-5">
                <h3 className="font-semibold text-gray-900 mb-2">{item.q}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact strip */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-orange-100 rounded-2xl mb-4">
            <Clock className="w-7 h-7 text-orange-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">لسه محتاج مساعدة؟</h3>
          <p className="text-gray-600 mb-6">ساعات عملنا من السبت إلى الخميس، من 9:00 صباحاً حتى 6:00 مساءً</p>
          <Button size="lg" className="bg-orange-600 hover:bg-orange-700" onClick={() => navigate('/contact')}>
            تواصل معنا
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-blue-950 text-gray-400 py-12 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src="/icon-512-from-server.png" alt="Target Express Logo" className="h-12 w-12" />
                <div>
                  <span className="text-lg font-bold text-white block leading-tight">Target Express</span>
                  <span className="text-[10px] text-gray-400 block leading-tight">Shipping, Logistics &amp; Fulfillment Solutions</span>
                </div>
              </div>
              <p className="text-sm leading-relaxed">شريكك اللوجستي الموثوق في مصر. نوفر حلول شحن مبتكرة وسريعة لتنمية أعمالك.</p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">روابط سريعة</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/about" className="hover:text-orange-500 transition">عن الشركة</a></li>
                <li><a href="/#features" className="hover:text-orange-500 transition">خدماتنا</a></li>
                <li><a href="/pricing" className="hover:text-orange-500 transition">الأسعار</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">الدعم</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/support" className="hover:text-orange-500 transition">مركز المساعدة</a></li>
                <li><a href="/track" className="hover:text-orange-500 transition">تتبع شحنتك</a></li>
                <li><a href="/contact" className="hover:text-orange-500 transition">اتصل بنا</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">تواصل معنا</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2"><MapPin className="w-4 h-4" /> القاهرة، مصر</li>
                <li className="flex items-center gap-2"><Smartphone className="w-4 h-4" /> 01008524717</li>
                <li className="flex items-center gap-2">support@targetexpress.com</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 text-center text-sm">
            © {new Date().getFullYear()} Target Express. جميع الحقوق محفوظة.
          </div>
        </div>
      </footer>
    </div>
  );
}
